import { Link } from 'react-router-dom';
import RUOBadge from '@/components/RUOBadge';
import { LEGAL_CONFIG } from '@/legal/config';

const COLUMNS = [
  {
    title: 'Legal',
    links: [
      ['/legal/terms', 'Terms of Use', 'terms'],
      ['/legal/privacy', 'Privacy Policy', 'privacy'],
      ['/legal/ruo', 'Research Use Only', 'ruo'],
      ['/legal/cookies', 'Cookies', 'cookies'],
    ],
  },
  {
    title: 'Compliance',
    links: [
      ['/legal/ai-transparency', 'AI Transparency', 'ai'],
      ['/legal/ai-act', 'EU AI Act', 'aiact'],
      ['/legal/dpa', 'DPA (Art. 28)', 'dpa'],
      ['/legal/subprocessors', 'Subprocessors', 'subprocessors'],
    ],
  },
  {
    title: 'Product',
    links: [
      ['/guides', 'User guides', 'guides'],
      ['/guide/dissertation', 'Dissertation guide', 'guide-dissertation'],
      ['/thesis/billing', 'Plans & pricing', 'billing-plans'],
      ['/legal/billing', 'Billing & Invoices', 'billing'],
    ],
  },
  {
    title: 'Company',
    links: [
      ['/about', 'About InkPhD', 'about'],
      ['/journal-authors', 'For journal authors', 'journal-authors'],
      ['/contact', 'Contact us', 'contact'],
      ['/thesis/account', 'Data & privacy', 'account'],
    ],
  },
];

/** Global footer: four link columns, no published email addresses (use the contact form). */
export default function AppFooter({ lang = 'en' }) {
  void lang;
  const year = new Date().getFullYear();

  return (
    <footer className="mt-16 border-t border-border bg-muted/30 print:hidden" data-testid="app-footer">
      <div className="max-w-6xl mx-auto px-4 py-10 grid gap-8 md:grid-cols-3 lg:grid-cols-6 text-sm">
        <div className="space-y-2 lg:col-span-2">
          <div className="flex items-center gap-2 font-serif font-bold">
            {LEGAL_CONFIG.product.name}
            <RUOBadge lang={lang} />
          </div>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {LEGAL_CONFIG.product.positioning_en}
          </p>
          <p className="text-[11px] text-muted-foreground">
            Not a medical device. Not for clinical decision-making.
          </p>
          <p className="text-[11px] text-muted-foreground pt-1">
            Questions? Use the{' '}
            <Link to="/contact" className="underline hover:text-foreground" data-testid="footer-contact-cta">
              contact form
            </Link>{' '}
            — we reply within two working days.
          </p>
        </div>

        {COLUMNS.map((col) => (
          <div key={col.title} className="space-y-2">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">
              {col.title}
            </div>
            <ul className="space-y-1.5 text-xs">
              {col.links.map(([to, label, id]) => (
                <li key={id}>
                  <Link to={to} className="hover:text-[#1d4ed8] dark:hover:text-[#7aa9ff] transition-colors"
                    data-testid={`footer-link-${id}`}>
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-border">
        <div className="max-w-6xl mx-auto px-4 py-3 text-[11px] text-muted-foreground
          flex flex-wrap items-center gap-x-3 gap-y-1">
          <span>© {year} {LEGAL_CONFIG.product.name}. All rights reserved.</span>
          <span className="opacity-60">·</span>
          <span>{LEGAL_CONFIG.controller.legal_name}</span>
        </div>
      </div>
    </footer>
  );
}
